import {BRANCH, SUCCESS_STRING, ERROR_STRING, TIMEOUT_STRING, SKYLARK_PROVISIONING_ACTIONS} from "../../../util/Constants";

/**
 * Validates if the provisioning message has all required fields
 * @param {Object} data - The provisioning message data
 * @returns {boolean} - Whether the message is valid
 */
const isValidProvisioningMessage = (data) => {
    return data &&
        data.nodeType &&
        (data.nodeType === BRANCH || data.parentNodeIndex !== undefined) &&
        data.expectedNodeCount !== undefined &&
        data.detectedNodeCount !== undefined;
};

/**
 * Handles timeout for provisioning operations
 * @param {Object} state - Current state
 * @returns {Object} - New state with timeout status if applicable
 */
const checkProvisioningTimeout = (state) => {
    // If provisioning is triggered but no status after timeout period, consider it timed out
    if (state.provisioningTriggered &&
        state.provisioningStartTime &&
        !state.clusterStatus &&
        Date.now() - state.provisioningStartTime > 30000) {
        return {
            ...state,
            clusterStatus: TIMEOUT_STRING
        };
    }
    return state;
};

export const ProvisioningReducer = (state, action) => {
    // First check for timeout in any operation
    let currentState = checkProvisioningTimeout(state);

    switch (action.type) {
        case SKYLARK_PROVISIONING_ACTIONS.INIT_PROVISIONING:
            return {
                ...currentState,
                provisioningTriggered: true,
                provisioningData: [],
                clusterStatus: null,
                provisioningStartTime: Date.now(),
                lastUpdated: Date.now(),
                expectedBranchNodeCount: null,
                detectedBranchNodeCount: null
            };
        case SKYLARK_PROVISIONING_ACTIONS.RESET_PROVISIONING:
            return {
                ...currentState,
                provisioningTriggered: false,
                provisioningData: [],
                clusterStatus: null,
                provisioningStartTime: null,
                lastUpdated: Date.now(),
                expectedBranchNodeCount: null,
                detectedBranchNodeCount: null
            };
        case SKYLARK_PROVISIONING_ACTIONS.SET_CLUSTER_STATUS:
            return {
                ...currentState,
                clusterStatus: action.data.status,
                lastUpdated: Date.now()
            };
        case SKYLARK_PROVISIONING_ACTIONS.UPDATE_PROVISIONING_DATA:
            try {
                const parsedData = action.data.message;

                // Validate the required fields
                if (!isValidProvisioningMessage(parsedData)) {
                    console.error("Invalid provisioning data format:", parsedData);
                    return currentState;
                }

                // If provisioning hasn't been triggered yet but we're receiving messages,
                // auto-initialize the provisioning state
                let newState;
                if (!currentState.provisioningTriggered) {
                    newState = {
                        ...currentState,
                        provisioningTriggered: true,
                        provisioningData: [],
                        provisioningStartTime: Date.now()
                    };
                } else {
                    newState = { ...currentState };
                }

                // Handle branch provisioning status
                if (parsedData.nodeType === BRANCH) {
                    // Update cluster status based on whether expected matches detected
                    const isSuccess = parsedData.expectedNodeCount === parsedData.detectedNodeCount;
                    newState.clusterStatus = isSuccess ? SUCCESS_STRING : ERROR_STRING;
                    newState.expectedBranchNodeCount = parsedData.expectedNodeCount;
                    newState.detectedBranchNodeCount = parsedData.detectedNodeCount;
                }

                // Add the new data to our state
                const existingIndex = currentState.provisioningData.findIndex(
                    item => item.nodeType === parsedData.nodeType &&
                        (parsedData.nodeType === BRANCH || item.parentNodeIndex === parsedData.parentNodeIndex)
                );

                if (existingIndex >= 0) {
                    // Update existing entry
                    const newData = [...currentState.provisioningData];
                    newData[existingIndex] = {
                        ...parsedData,
                        timestamp: Date.now() // Add timestamp for tracking
                    };
                    newState.provisioningData = newData;
                } else {
                    // Add new entry
                    newState.provisioningData = [
                        ...currentState.provisioningData,
                        {
                            ...parsedData,
                            timestamp: Date.now() // Add timestamp for tracking
                        }
                    ];
                }

                newState.lastUpdated = Date.now();
                return newState;
            } catch (error) {
                console.error("Error in UPDATE_PROVISIONING_DATA:", error);
                return currentState;
            }
        default:
            return currentState;
    }
};

export const initialProvisioningState = {
    provisioningTriggered: false,
    provisioningData: [],
    clusterStatus: null, // null, 'success', 'error', 'timeout'
    provisioningStartTime: null,
    lastUpdated: null,
    expectedBranchNodeCount: null,
    detectedBranchNodeCount: null
};



// WEBPACK FOOTER //
// ./src/components/devicemanagement/lidar/reducer/ProvisioningReducer.js